import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Bell, CalendarClock, AlertTriangle, PauseCircle, CheckCircle, XCircle,
  RotateCcw, X, Check, Sparkles, Loader2,
} from 'lucide-react';
import { toast } from 'sonner';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from './ui/sheet';
import { Button } from './ui/button';
import { notificationsAPI } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';

/**
 * NotificationBell — icon in the site header for logged-in customers.
 * Opens a bottom sheet with two tabs:
 *   • Active — unread + read (not dismissed) notifications, mark-read/dismiss
 *   • Dismissed — recycling bin, "restore" brings items back into Active
 *
 * Sources of notifications:
 *   • Installment lifecycle (due-soon, due-today, overdue, paid, suspended,
 *     reactivated, fully-paid)
 *   • Package lifecycle (expiring-soon, expired)
 *
 * The bell polls once per page load + on sheet open. We deliberately keep
 * this lightweight (no websocket) — every installment cron / FPX callback
 * now feeds the same collection.
 */
const ICONS = {
  'calendar-clock': CalendarClock,
  'alert-triangle': AlertTriangle,
  'pause-circle': PauseCircle,
  'check-circle': CheckCircle,
  'x-circle': XCircle,
};

const SEVERITY_STYLES = {
  info: 'border-slate-200 bg-white',
  success: 'border-emerald-200 bg-emerald-50/60',
  warning: 'border-amber-200 bg-amber-50/60',
  critical: 'border-red-200 bg-red-50/60',
};

const SEVERITY_ICON_TINT = {
  info: 'text-slate-500',
  success: 'text-emerald-700',
  warning: 'text-amber-800',
  critical: 'text-red-700',
};

const formatWhen = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    const now = new Date();
    const mins = Math.round((now - d) / 60000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins}m ago`;
    if (mins < 60 * 24) return `${Math.round(mins / 60)}h ago`;
    return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' });
  } catch (_) { return ''; }
};

const NotificationBell = () => {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState('active');
  const [items, setItems] = useState([]);
  const [dismissedItems, setDismissedItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(null);

  const isCustomer = !!user && user.role !== 'admin' && user.role !== 'coach';

  const refreshCount = useCallback(async () => {
    if (!isCustomer) { setUnread(0); return; }
    try {
      const { data } = await notificationsAPI.unreadCount();
      setUnread(data?.unread_count || 0);
    } catch (_) { /* silent */ }
  }, [isCustomer]);

  const loadActive = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await notificationsAPI.list({ include_dismissed: false });
      setItems(data?.items || []);
      setUnread(data?.unread_count || 0);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load notifications');
    } finally { setLoading(false); }
  }, []);

  const loadDismissed = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await notificationsAPI.list({ include_dismissed: true });
      const all = data?.items || [];
      setDismissedItems(all.filter((n) => n.dismissed_at));
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load notifications');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { refreshCount(); }, [refreshCount]);
  useEffect(() => {
    if (!open) return;
    if (tab === 'active') loadActive();
    else loadDismissed();
  }, [open, tab, loadActive, loadDismissed]);

  const markRead = async (n) => {
    if (n.read_at) return;
    setBusy(`read-${n.id}`);
    try {
      await notificationsAPI.markRead(n.id);
      setItems((prev) => prev.map((it) => it.id === n.id ? { ...it, read_at: new Date().toISOString() } : it));
      setUnread((u) => Math.max(0, u - 1));
    } catch (_) { /* silent */ }
    finally { setBusy(null); }
  };

  const dismiss = async (n) => {
    setBusy(`dismiss-${n.id}`);
    try {
      await notificationsAPI.dismiss(n.id);
      setItems((prev) => prev.filter((it) => it.id !== n.id));
      if (!n.read_at) setUnread((u) => Math.max(0, u - 1));
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not dismiss');
    } finally { setBusy(null); }
  };

  const restore = async (n) => {
    setBusy(`restore-${n.id}`);
    try {
      await notificationsAPI.restore(n.id);
      setDismissedItems((prev) => prev.filter((it) => it.id !== n.id));
      toast.success('Restored to inbox');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not restore');
    } finally { setBusy(null); }
  };

  const markAll = async () => {
    setBusy('markall');
    try {
      await notificationsAPI.markAllRead();
      setItems((prev) => prev.map((it) => ({ ...it, read_at: it.read_at || new Date().toISOString() })));
      setUnread(0);
    } catch (_) { /* silent */ }
    finally { setBusy(null); }
  };

  if (!isCustomer) return null;

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label={unread ? `${unread} unread notifications` : 'Notifications'}
        className="relative h-9 w-9 sm:h-10 sm:w-10 inline-flex items-center justify-center rounded-full border border-slate-200 bg-white text-[#3F2774] hover:border-[#A855F7] hover:bg-[#F5F3FF] transition-colors"
        data-testid="notification-bell"
      >
        <Bell size={16} />
        {unread > 0 && (
          <span
            className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-red-600 text-white text-[10px] font-black flex items-center justify-center"
            data-testid="notification-bell-badge"
          >
            {unread > 9 ? '9+' : unread}
          </span>
        )}
      </button>

      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent
          side="right"
          className="w-full sm:max-w-md p-0 flex flex-col"
          data-testid="notifications-sheet"
        >
          <SheetHeader className="px-5 pt-5 pb-3 border-b border-slate-100 text-left">
            <div className="flex items-center justify-between gap-3">
              <SheetTitle className="flex items-center gap-2 text-[#3F2774]">
                <Bell size={18} className="text-[#7E22CE]" /> Notifications
              </SheetTitle>
              {tab === 'active' && items.some((i) => !i.read_at) && (
                <button
                  type="button"
                  onClick={markAll}
                  disabled={busy === 'markall'}
                  className="text-[11px] font-black uppercase tracking-widest text-[#7E22CE] hover:text-[#4C1D95] disabled:opacity-50"
                  data-testid="notifications-mark-all-read"
                >
                  Mark all read
                </button>
              )}
            </div>
            <div className="mt-3 inline-flex rounded-md border border-slate-200 bg-slate-50 p-1 text-[11px] font-black uppercase tracking-widest">
              <button
                type="button"
                onClick={() => setTab('active')}
                className={`px-3 py-1 rounded transition-colors ${tab === 'active' ? 'bg-white text-[#3F2774] shadow-sm' : 'text-slate-600 hover:text-[#3F2774]'}`}
                data-testid="notifications-tab-active"
              >
                Active {unread > 0 && <span className="ml-1 text-red-600">{unread}</span>}
              </button>
              <button
                type="button"
                onClick={() => setTab('dismissed')}
                className={`px-3 py-1 rounded transition-colors ${tab === 'dismissed' ? 'bg-white text-[#3F2774] shadow-sm' : 'text-slate-600 hover:text-[#3F2774]'}`}
                data-testid="notifications-tab-dismissed"
              >
                Dismissed
              </button>
            </div>
          </SheetHeader>

          <div className="flex-1 overflow-y-auto px-5 py-4" data-testid="notifications-list">
            {loading ? (
              <div className="flex items-center justify-center py-16 text-slate-500 text-sm">
                <Loader2 size={16} className="animate-spin mr-2" /> Loading…
              </div>
            ) : tab === 'active' ? (
              items.length === 0 ? (
                <EmptyState kind="active" />
              ) : (
                <ul className="space-y-3">
                  {items.map((n) => (
                    <NotificationRow
                      key={n.id}
                      n={n}
                      busy={busy}
                      onOpen={() => markRead(n)}
                      onDismiss={() => dismiss(n)}
                    />
                  ))}
                </ul>
              )
            ) : (
              dismissedItems.length === 0 ? (
                <EmptyState kind="dismissed" />
              ) : (
                <ul className="space-y-3">
                  {dismissedItems.map((n) => (
                    <NotificationRow
                      key={n.id}
                      n={n}
                      busy={busy}
                      dismissedTab
                      onRestore={() => restore(n)}
                    />
                  ))}
                </ul>
              )
            )}
          </div>

          <div className="border-t border-slate-100 px-5 py-3 flex items-center justify-between text-[11px] text-slate-500">
            <span>Installment + package alerts sent by LGRA.</span>
            <Button variant="ghost" size="sm" onClick={() => setOpen(false)} data-testid="notifications-close">
              <X size={12} className="mr-1" /> Close
            </Button>
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
};

const EmptyState = ({ kind }) => (
  <div className="flex flex-col items-center justify-center text-center py-12 text-slate-500">
    <Sparkles size={20} className="text-[#A855F7] mb-2" />
    <p className="text-sm font-semibold text-slate-700">
      {kind === 'dismissed' ? 'No dismissed notifications' : "You're all caught up"}
    </p>
    <p className="text-xs mt-1 max-w-[26ch]">
      {kind === 'dismissed'
        ? 'Notifications you dismiss appear here so you can restore them anytime.'
        : "We'll ping you when an installment payment is due or a package needs attention."}
    </p>
  </div>
);

const NotificationRow = ({ n, busy, dismissedTab = false, onOpen, onDismiss, onRestore }) => {
  const Icon = ICONS[n.icon || 'calendar-clock'] || Bell;
  const tone = SEVERITY_STYLES[n.severity || 'info'];
  const iconTint = SEVERITY_ICON_TINT[n.severity || 'info'];
  const unread = !n.read_at;
  return (
    <li
      className={`relative rounded-lg border p-3 ${tone} ${unread ? 'ring-2 ring-[#5530B5]/20' : ''}`}
      data-testid={`notification-row-${n.kind}`}
    >
      <div className="flex items-start gap-3">
        <span className={`mt-0.5 w-8 h-8 rounded-full bg-white border border-slate-200 flex items-center justify-center shrink-0 ${iconTint}`}>
          <Icon size={16} />
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-2">
            <p className="text-sm font-black text-[#101B3A] leading-snug">
              {n.title}
              {unread && (
                <span className="ml-2 inline-block w-2 h-2 rounded-full bg-[#5530B5] align-middle" aria-hidden />
              )}
            </p>
            <span className="text-[10px] text-slate-500 whitespace-nowrap">{formatWhen(n.created_at)}</span>
          </div>
          {n.body && (
            <p className="text-[13px] text-slate-700 mt-1 leading-snug">{n.body}</p>
          )}
          {n.family_profile_name && (
            <span
              className="mt-2 inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-widest text-[#5530B5] bg-[#F5F3FF] border border-[#D8C4FF] rounded-full px-2 py-0.5"
              data-testid="notification-family-tag"
            >
              For {n.family_profile_name}
            </span>
          )}
          <div className="mt-2 flex flex-wrap items-center gap-2">
            {n.action_url && !dismissedTab && (
              <Link
                to={n.action_url}
                onClick={onOpen}
                className="inline-flex items-center gap-1 text-[11px] font-black uppercase tracking-widest text-white bg-[#5530B5] hover:bg-[#3F2774] rounded px-2 py-1"
                data-testid="notification-action"
              >
                {n.action_label || 'View'}
              </Link>
            )}
            {dismissedTab ? (
              <button
                type="button"
                onClick={onRestore}
                disabled={busy === `restore-${n.id}`}
                className="inline-flex items-center gap-1 text-[11px] font-black uppercase tracking-widest text-[#5530B5] hover:text-[#3F2774] disabled:opacity-50"
                data-testid="notification-restore"
              >
                <RotateCcw size={11} /> Restore
              </button>
            ) : (
              <>
                {unread && (
                  <button
                    type="button"
                    onClick={onOpen}
                    disabled={busy === `read-${n.id}`}
                    className="inline-flex items-center gap-1 text-[11px] font-black uppercase tracking-widest text-slate-600 hover:text-[#3F2774] disabled:opacity-50"
                    data-testid="notification-mark-read"
                  >
                    <Check size={11} /> Mark read
                  </button>
                )}
                <button
                  type="button"
                  onClick={onDismiss}
                  disabled={busy === `dismiss-${n.id}`}
                  className="inline-flex items-center gap-1 text-[11px] font-black uppercase tracking-widest text-slate-500 hover:text-red-700 disabled:opacity-50"
                  data-testid="notification-dismiss"
                >
                  <X size={11} /> Dismiss
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </li>
  );
};

export default NotificationBell;
